import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  LogOut,
  Loader2,
  Link as LinkIcon,
  MessageSquare,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { useTranslation, useLocale, useLocaleContext, LOCALE_CONFIG } from '@parentpath/i18n';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { supabase } from '@/lib/supabase';
import { getMetadataDisplayName, getUserDisplayName } from '@/lib/userName';
import { BabyNamesModal, BabyNameEntry } from '@/components/BabyNamesModal';
import { DueDateModal } from '@/components/DueDateModal';
import { CompletePregnancyModal } from '@/components/CompletePregnancyModal';
import { DeleteAccountModal } from '@/components/DeleteAccountModal';
import { useAppMode } from '@/hooks/useAppMode';
import { useAppVersion } from '@/hooks/useAppVersion';
import { BabyData } from '@/store/pregnancyStore';
import { EditNameModal } from '@/components/EditNameModal';
import { EditGenderModal } from '@/components/EditGenderModal';
import { EditPregnancyTypeModal } from '@/components/EditPregnancyTypeModal';
import { ChangeEmailModal } from '@/components/ChangeEmailModal';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { ConsentManagementModal } from '@/components/ConsentManagementModal';
import { EditBirthDataModal } from '@/components/EditBirthDataModal';
import { LanguageModal } from '@/components/LanguageModal';
import { TimezoneModal, getTimezoneDisplayLabel } from '@/components/TimezoneModal';
import { usePartner } from '@/features/partner/hooks/usePartner';
import { useSubscription, useCreatePortalSession } from '@/features/subscription';
import { useLimitEnabled } from '@/hooks/useLimits';
import { ColorPickerSection } from '@/components/ColorPickerSection';

type UserRole = 'mother' | 'father' | 'partner';
type Gender = 'female' | 'male' | 'other';

interface ProfileData {
  birth_date: string | null;
  gender: Gender | null;
  user_role: UserRole | null;
  first_name: string | null;
  last_name: string | null;
}

export default function ProfilePage() {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const appVersion = useAppVersion();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { currentPregnancy, currentSSW, setPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();

  // In baby mode, use lastCompletedPregnancy for partner connection
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const activePregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);

  const { partner, isConnected, isPending } = usePartner(activePregnancyId);
  const { isPremium, isLoading: subscriptionLoading, status: subscriptionStatus, premiumExpiresAt, isBetaTester } = useSubscription();
  const portalSession = useCreatePortalSession();
  const customColorsEnabled = useLimitEnabled('custom_colors');
  const { completeBirth, refreshAppMode } = useAppMode();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    birth_date: null,
    gender: null,
    user_role: null,
    first_name: null,
    last_name: null,
  });
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [showBabyNamesModal, setShowBabyNamesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showEditGenderModal, setShowEditGenderModal] = useState(false);
  const [showEditPregnancyTypeModal, setShowEditPregnancyTypeModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showPregnancyTypePrompt, setShowPregnancyTypePrompt] = useState(false);
  const [pendingBabyNames, setPendingBabyNames] = useState<BabyNameEntry[] | null>(null);
  const [showNewPregnancyConfirm, setShowNewPregnancyConfirm] = useState(false);
  const [isResettingData, setIsResettingData] = useState(false);
  const [showBirthDataModal, setShowBirthDataModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [userTimezone, setUserTimezone] = useState('Europe/Berlin');
  const { locale } = useLocale();
  const { isLocaleEnabled } = useLocaleContext();

  // Use first_name + last_name if available, otherwise fall back to display_name
  const getDisplayName = () => {
    if (profileData.first_name) {
      return profileData.last_name
        ? `${profileData.first_name} ${profileData.last_name}`
        : profileData.first_name;
    }
    return getUserDisplayName(user);
  };
  const displayName = getDisplayName();

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('birth_date, gender, user_role, first_name, last_name, timezone')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setProfileData({
          birth_date: data?.birth_date || null,
          gender: data?.gender || null,
          user_role: data?.user_role || null,
          first_name: data?.first_name || null,
          last_name: data?.last_name || null,
        });
        if (data?.timezone) {
          setUserTimezone(data.timezone);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [user?.id]);

  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateAge = (birthDateStr: string | null): number | null => {
    if (!birthDateStr) return null;
    const birth = new Date(birthDateStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSaveDueDate = async (newDueDate: string) => {
    if (!currentPregnancy || !newDueDate) return;
    const { error } = await supabase
      .from('pregnancies')
      .update({ expected_due_date: newDueDate })
      .eq('id', currentPregnancy.id);
    if (error) throw error;
    setPregnancy({ ...currentPregnancy, expected_due_date: newDueDate });
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    await signOut();
    navigate('/login');
  };

  const handleSaveBabyNames = async (names: BabyNameEntry[]) => {
    if (!currentPregnancy) return;

    // Check if more names than current child count
    const filteredNames = names.filter((n) => n.name.trim() !== '');
    if (filteredNames.length > currentPregnancy.child_count) {
      // Store the names and show prompt
      setPendingBabyNames(names);
      setShowPregnancyTypePrompt(true);
      return;
    }

    await saveBabyNamesDirectly(names);
  };

  const saveBabyNamesDirectly = async (names: BabyNameEntry[]) => {
    if (!currentPregnancy) return;
    const { error } = await supabase
      .from('pregnancies')
      .update({ baby_names: names })
      .eq('id', currentPregnancy.id);
    if (error) throw error;
    setPregnancy({ ...currentPregnancy, baby_names: names as unknown as string[] });
  };

  // Save baby names in baby mode (updates both baby_names and baby_data.babies)
  const handleSaveBabyNamesBabyMode = async (names: BabyNameEntry[]) => {
    if (!lastCompletedPregnancy) return;

    // Update baby_data.babies names to match
    const existingBabyData = lastCompletedPregnancy.baby_data || {};
    const existingBabies = existingBabyData.babies || [];
    const updatedBabies = names.map((n, i) => ({
      ...(existingBabies[i] || {}),
      name: n.name || undefined,
      gender: n.gender === 'neutral' ? undefined : n.gender,
    }));

    const updatedBabyData = { ...existingBabyData, babies: updatedBabies };

    const { error } = await supabase
      .from('pregnancies')
      .update({
        baby_names: names,
        baby_data: updatedBabyData,
      })
      .eq('id', lastCompletedPregnancy.id);
    if (error) throw error;

    // Update store so changes take effect across the app
    const { setLastCompletedPregnancy } = usePregnancyStore.getState();
    setLastCompletedPregnancy({
      ...lastCompletedPregnancy,
      baby_names: names as unknown as string[],
      baby_data: updatedBabyData as BabyData,
    });
  };

  // Save birth data (date, time, baby measurements) in baby mode
  const handleSaveBirthData = async (birthDate: string, birthTime: string | null, babyData: BabyData) => {
    if (!lastCompletedPregnancy) return;

    const { error } = await supabase
      .from('pregnancies')
      .update({
        actual_birth_date: birthDate,
        birth_time: birthTime,
        baby_data: babyData,
      })
      .eq('id', lastCompletedPregnancy.id);

    if (error) throw error;

    // Update store
    const { setLastCompletedPregnancy } = usePregnancyStore.getState();
    setLastCompletedPregnancy({
      ...lastCompletedPregnancy,
      actual_birth_date: birthDate,
      birth_time: birthTime,
      baby_data: babyData,
    });
  };

  const handlePregnancyTypePromptConfirm = async () => {
    if (!pendingBabyNames || !currentPregnancy) return;
    // User wants to change pregnancy type - open the pregnancy type modal
    setShowPregnancyTypePrompt(false);
    // First save the names
    await saveBabyNamesDirectly(pendingBabyNames);
    setPendingBabyNames(null);
    // Then open the pregnancy type modal
    setShowEditPregnancyTypeModal(true);
  };

  const handlePregnancyTypePromptCancel = async () => {
    if (!pendingBabyNames) return;
    // User doesn't want to change pregnancy type - save names anyway
    await saveBabyNamesDirectly(pendingBabyNames);
    setShowPregnancyTypePrompt(false);
    setPendingBabyNames(null);
  };

  const handleSavePregnancyType = (childCount: number) => {
    if (currentPregnancy) {
      setPregnancy({ ...currentPregnancy, child_count: childCount });
    }
  };

  const handleSaveName = (firstName: string, lastName: string) => {
    setProfileData((prev) => ({
      ...prev,
      first_name: firstName,
      last_name: lastName,
    }));
    // The displayName will update on next render since it depends on profileData
  };

  const handleSaveGender = (gender: Gender) => {
    setProfileData((prev) => ({ ...prev, gender }));
  };

  const parseBabyNames = (names: unknown): BabyNameEntry[] => {
    if (!names || !Array.isArray(names) || names.length === 0) return [];
    if (typeof names[0] === 'string') {
      return names.map((name: string) => ({ name, gender: 'neutral' as const }));
    }
    return names as BabyNameEntry[];
  };

  // Handle completion with birth
  const handleCompleteBirth = async (
    birthDate: string,
    birthTime?: string,
    babyData?: BabyData
  ): Promise<boolean> => {
    const success = await completeBirth(birthDate, birthTime, babyData as Record<string, unknown> | undefined);
    if (success) {
      await refreshAppMode();
      setPregnancy(null);
      setShowDeleteModal(false);
      navigate('/');
    }
    return success;
  };

  // Handle delete/end pregnancy (all data)
  const handleDeletePregnancy = async () => {
    if (!currentPregnancy || !user) return;

    // Get partner's user_id before we modify the connection
    const { data: partnerConnection } = await supabase
      .from('partner_connections')
      .select('user_1_id, user_2_id')
      .eq('pregnancy_id', currentPregnancy.id)
      .eq('status', 'active')
      .maybeSingle();

    // Get partner user ID (if connected)
    let partnerUserId: string | null = null;
    if (partnerConnection) {
      partnerUserId = partnerConnection.user_1_id === user.id
        ? partnerConnection.user_2_id
        : partnerConnection.user_1_id;
    }

    // Delete associated data
    await supabase.from('user_appointments').delete().eq('pregnancy_id', currentPregnancy.id);
    const { data: checklists } = await supabase.from('user_checklists').select('id').eq('pregnancy_id', currentPregnancy.id);
    if (checklists && checklists.length > 0) {
      await supabase.from('user_checklist_items').delete().in('checklist_id', checklists.map(c => c.id));
      await supabase.from('user_checklists').delete().eq('pregnancy_id', currentPregnancy.id);
    }
    await supabase.from('name_swipes').delete().eq('pregnancy_id', currentPregnancy.id);
    await supabase.from('name_matches').delete().eq('pregnancy_id', currentPregnancy.id);

    // Delete activity feed entries for this pregnancy (user and partner)
    await supabase.from('activity_feed').delete().eq('pregnancy_id', currentPregnancy.id);
    // Also delete activities for user_id that might have pregnancy_id = null but belong to this period
    await supabase.from('activity_feed').delete().eq('user_id', user.id);
    if (partnerUserId) {
      await supabase.from('activity_feed').delete().eq('user_id', partnerUserId);
    }

    // Delete pregnancy info
    await supabase.from('pregnancy_info').delete().eq('pregnancy_id', currentPregnancy.id);

    // Set partner connection to 'pregnancy_deleted' status instead of deleting
    // This allows the partner to be notified when they open the app
    await supabase
      .from('partner_connections')
      .update({ status: 'pregnancy_deleted' })
      .eq('pregnancy_id', currentPregnancy.id)
      .eq('status', 'active');

    // Delete pending invitations
    await supabase
      .from('partner_connections')
      .delete()
      .eq('pregnancy_id', currentPregnancy.id)
      .eq('status', 'pending');

    // Delete the pregnancy
    await supabase.from('pregnancies').delete().eq('id', currentPregnancy.id);

    setPregnancy(null);
    setShowDeleteModal(false);
    navigate('/onboarding');
  };

  const handleManageSubscriptionForDelete = async () => {
    try {
      const result = await portalSession.mutateAsync(`${window.location.origin}/profile`);
      if (result.url) window.location.href = result.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    const { error } = await supabase.rpc('delete_own_account');
    if (error) {
      console.error('Delete account error:', error);
      throw error;
    }

    await signOut();
    navigate('/login');
  };

  // Handle starting a new pregnancy - deletes all existing data
  const handleStartNewPregnancy = async () => {
    if (!user) return;
    setIsResettingData(true);

    try {
      // Get all pregnancies (active + completed) for this user
      const { data: pregnancies } = await supabase
        .from('pregnancies')
        .select('id')
        .eq('created_by', user.id);

      if (pregnancies && pregnancies.length > 0) {
        for (const pregnancy of pregnancies) {
          // Delete appointments
          await supabase.from('user_appointments').delete().eq('pregnancy_id', pregnancy.id);

          // Delete checklist items and checklists
          const { data: checklists } = await supabase
            .from('user_checklists')
            .select('id')
            .eq('pregnancy_id', pregnancy.id);
          if (checklists && checklists.length > 0) {
            await supabase.from('user_checklist_items').delete().in('checklist_id', checklists.map(c => c.id));
            await supabase.from('user_checklists').delete().eq('pregnancy_id', pregnancy.id);
          }

          // Delete name swipes and matches
          await supabase.from('name_swipes').delete().eq('pregnancy_id', pregnancy.id);
          await supabase.from('name_matches').delete().eq('pregnancy_id', pregnancy.id);

          // Delete activity feed entries
          await supabase.from('activity_feed').delete().eq('pregnancy_id', pregnancy.id);

          // Delete pregnancy info
          await supabase.from('pregnancy_info').delete().eq('pregnancy_id', pregnancy.id);

          // Set partner connections to 'pregnancy_deleted' for active connections
          await supabase
            .from('partner_connections')
            .update({ status: 'pregnancy_deleted', is_family_connection: false })
            .eq('pregnancy_id', pregnancy.id)
            .in('status', ['active', 'pending']);
        }

        // Delete all pregnancies
        await supabase.from('pregnancies').delete().eq('created_by', user.id);
      }

      // Also check if user is connected as partner to someone else's pregnancy
      // Set those connections to removed so partner gets prompted for onboarding
      await supabase
        .from('partner_connections')
        .update({ status: 'removed' })
        .eq('user_2_id', user.id)
        .in('status', ['active']);

      // Reset pregnancy store
      setPregnancy(null);
      await refreshAppMode();

      // Navigate to onboarding
      setShowNewPregnancyConfirm(false);
      navigate('/onboarding');
    } catch (error) {
      console.error('Error resetting pregnancy data:', error);
    } finally {
      setIsResettingData(false);
    }
  };

  const age = calculateAge(profileData.birth_date);
  const parsedBabyNames = parseBabyNames(currentPregnancy?.baby_names);
  const babyNamesDisplay = parsedBabyNames.length > 0 ? parsedBabyNames.map(e => e.name).join(', ') : t('common:notSet');

  // Baby mode: extract names from completed pregnancy's baby_data
  const babyModeBabyNames: BabyNameEntry[] = (() => {
    if (!isBabyMode || !lastCompletedPregnancy) return [];
    const babies = lastCompletedPregnancy.baby_data?.babies;
    if (babies && babies.length > 0) {
      return babies.map(b => ({
        name: b.name || '',
        gender: (b.gender === 'male' || b.gender === 'female' ? b.gender : 'neutral') as 'male' | 'female' | 'neutral',
      }));
    }
    return parseBabyNames(lastCompletedPregnancy.baby_names);
  })();
  const babyModeNamesDisplay = babyModeBabyNames.filter(n => n.name).length > 0
    ? babyModeBabyNames.filter(n => n.name).map(n => n.name).join(', ')
    : t('common:notSet');
  const babyModeChildCount = lastCompletedPregnancy?.child_count || lastCompletedPregnancy?.baby_data?.babies?.length || 1;

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-2xl font-semibold mb-8">{t('profile:title')}</h1>

      {/* Content */}
      <div className="space-y-6">
          {/* KONTO Section */}
          <section>
            <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.account')}</h2>
            <div className="profile-card">
              <ProfileItemLink label={t('profile:fields.name')} value={displayName} onClick={() => setShowEditNameModal(true)} />
              <ProfileItemLink label={t('profile:fields.email')} value={user?.email} onClick={() => setShowChangeEmailModal(true)} />
              {!isLoadingProfile && (
                <>
                  <ProfileItem
                    label={t('profile:fields.birthday')}
                    value={profileData.birth_date ? t('profile:birthdayWithAge', { date: formatDate(profileData.birth_date), age }) : t('common:notSpecified')}
                  />
                  <ProfileItemLink
                    label={t('profile:fields.gender')}
                    value={profileData.gender ? t(`common:gender.${profileData.gender}`) : t('common:notSpecified')}
                    onClick={() => setShowEditGenderModal(true)}
                  />
                </>
              )}
              <ProfileItemLink label={t('profile:fields.changePassword')} onClick={() => setShowChangePasswordModal(true)} />
              {/* Infokarte - visible when there's an active pregnancy or in baby mode */}
              {(currentPregnancy || isBabyMode) && (
                <ProfileItemLink label={t('profile:fields.editInfoCard')} onClick={() => navigate('/profile/pregnancy-info')} />
              )}
              <ProfileItemLink label={t('profile:fields.manageConsent')} onClick={() => setShowConsentModal(true)} />
            </div>
          </section>

          {/* MITGLIEDSCHAFT Section */}
          <section>
            <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.membership')}</h2>
            <button
              onClick={() => navigate('/subscription')}
              className="action-card w-full"
            >
              <div className="action-card-icon bg-pp-dark/25">
                <img
                  src="https://parentpath.de/pp_logo-piktogram-w100.svg"
                  alt="ParentPath"
                  className="w-6 h-6"
                />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-medium">{t('profile:membership.manage')}</p>
                <p className={`text-sm ${isPremium ? 'text-pp-orange font-medium' : 'text-white/60'}`}>
                  {subscriptionLoading ? '...' : isPremium ? t('profile:membership.premium') : t('profile:membership.free')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-pp-orange" />
            </button>
          </section>

          {/* FARBSCHEMA Section - controlled via admin limits */}
          {customColorsEnabled && <ColorPickerSection />}

          {/* SCHWANGERSCHAFT Section - No active pregnancy (not baby mode) */}
          {!currentPregnancy && !isBabyMode && (
            <section>
              <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.pregnancy')}</h2>
              <button onClick={() => navigate('/onboarding')} className="action-card w-full">
                <div className="action-card-icon bg-pp-orange/20">
                  <svg className="w-5 h-5 text-pp-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">{t('profile:pregnancy.start')}</p>
                  <p className="text-white/60 text-sm">{t('profile:pregnancy.startNew')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-pp-orange" />
              </button>
            </section>
          )}

          {/* BABY-MODUS Section - Baby mode active */}
          {isBabyMode && lastCompletedPregnancy && (
            <section>
              <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.babyMode')}</h2>
              <div className="profile-card">
                <ProfileItemLink
                  label={t('profile:fields.babyName')}
                  value={babyModeNamesDisplay}
                  onClick={() => setShowBabyNamesModal(true)}
                />
                <ProfileItemLink
                  label={t('profile:fields.birthData')}
                  onClick={() => setShowBirthDataModal(true)}
                />
                <button
                  onClick={() => setShowNewPregnancyConfirm(true)}
                  className="profile-card-item flex items-center justify-between w-full text-white/70"
                >
                  <span className="profile-card-item-value">{t('profile:babyMode.startNewPregnancy')}</span>
                  <ChevronRight className="w-5 h-5 text-pp-orange" />
                </button>
              </div>
            </section>
          )}

          {/* SCHWANGERSCHAFT Section - Active pregnancy */}
          {currentPregnancy && (
            <section>
              <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.pregnancy')}</h2>
              <div className="profile-card">
                <ProfileItem label={t('profile:fields.currentSSW')} value={t('profile:pregnancy.sswValue', { week: currentSSW })} />
                <ProfileItemLink
                  label={t('profile:fields.pregnancyType')}
                  value={t(`common:childCount.${currentPregnancy.child_count}`, { defaultValue: t('common:childCount.1') })}
                  onClick={() => setShowEditPregnancyTypeModal(true)}
                />
                <ProfileItemLink
                  label={t('profile:fields.babyName')}
                  value={babyNamesDisplay}
                  onClick={() => setShowBabyNamesModal(true)}
                />
                <ProfileItemLink
                  label={t('profile:fields.dueDate')}
                  value={formatDate(currentPregnancy.expected_due_date)}
                  onClick={() => setShowDueDateModal(true)}
                />
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="profile-card-item flex items-center justify-between w-full text-white/70"
                >
                  <span className="profile-card-item-value text-pp-orange">{t('profile:pregnancy.complete')}</span>
                  <ChevronRight className="w-5 h-5 text-pp-orange" />
                </button>
              </div>
            </section>
          )}

          {/* PARTNER-VERBINDUNG Section */}
          <section>
            <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.partner')}</h2>
            <button onClick={() => navigate('/partner')} className="action-card w-full">
              <div className={`action-card-icon ${isConnected ? 'bg-pp-dark/25' : ''}`}>
                <LinkIcon className={`w-5 h-5 ${isConnected ? 'text-pp-green' : isPending ? 'text-pp-orange' : 'text-white'}`} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-medium">
                  {isConnected ? t('profile:partner.connection') : t('profile:partner.invite')}
                </p>
                <p className={`text-sm ${isConnected ? 'text-white/60' : isPending ? 'text-pp-orange' : 'text-white/60'}`}>
                  {isConnected && partner ? t('profile:partner.connectedWith', { name: partner.display_name || partner.email?.split('@')[0] }) : isPending ? t('profile:partner.invitationPending') : t('profile:partner.notConnected')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-pp-orange" />
            </button>
          </section>

          {/* APP-EINSTELLUNGEN Section */}
          <section>
            <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.settings')}</h2>
            <div className="profile-card">
              {isLocaleEnabled && (
                <ProfileItemLink
                  label={t('profile:fields.language')}
                  value={LOCALE_CONFIG[locale].nativeName}
                  onClick={() => setShowLanguageModal(true)}
                />
              )}
              <ProfileItemLink
                label={t('profile:fields.timezone')}
                value={getTimezoneDisplayLabel(userTimezone, i18n.language)}
                onClick={() => setShowTimezoneModal(true)}
              />
              <ProfileItemLink label={t('profile:fields.notifications')} onClick={() => navigate('/profile/notifications')} />
            </div>
          </section>

          {/* BETA-FEEDBACK Section - only visible for beta testers */}
          {isBetaTester && (
            <section>
              <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.betaFeedback')}</h2>
              <button onClick={() => navigate('/help/feedback')} className="action-card w-full">
                <div className="action-card-icon bg-pp-dark/25">
                  <MessageSquare className="w-5 h-5 text-pp-orange" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">{t('profile:feedback.give')}</p>
                  <p className="text-white/60 text-sm">{t('profile:feedback.helpImprove')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-pp-orange" />
              </button>
            </section>
          )}

          {/* HILFE & INFO Section */}
          <section>
            <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">{t('profile:sections.helpInfo')}</h2>
            <div className="profile-card">
              <ProfileItemLink label={t('profile:help.helpFaq')} onClick={() => navigate('/help')} />
              <ProfileItemLink label={t('profile:help.privacy')} onClick={() => navigate('/legal/privacy')} />
              <ProfileItemLink label={t('profile:help.terms')} onClick={() => navigate('/legal/terms')} />
              <ProfileItemLink label={t('profile:help.imprint')} onClick={() => navigate('/legal/imprint')} />
              <ProfileItemLink label={t('profile:help.gdprRequest')} onClick={() => navigate('/profile/gdpr')} />
              <button
                onClick={() => setShowDeleteAccountModal(true)}
                className="profile-card-item flex items-center justify-between w-full text-white/70"
              >
                <span className="profile-card-item-value">{t('profile:help.deleteAccount')}</span>
                <Trash2 className="w-5 h-5 text-pp-orange" />
              </button>
            </div>
          </section>

          {/* Abmelden Button */}
          <div className="flex justify-center pt-4">
            <button
              onClick={handleSignOut}
              disabled={isLoggingOut}
              className="bg-pp-red rounded-pp px-6 py-3 flex items-center gap-2 text-white font-medium disabled:opacity-50"
            >
              {isLoggingOut ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <LogOut className="w-5 h-5" />
              )}
              {t('common:actions.signOut')}
            </button>
          </div>

          {/* Version - nur nativ, im Web und bei Fehlern ausgeblendet */}
          {appVersion && (
            <p className="text-center text-xs text-pp-dark py-1">
              {t('profile:version.label')} {appVersion.version} ({appVersion.build})
            </p>
          )}
        </div>

      {/* Modals */}
      {currentPregnancy && (
        <>
          <BabyNamesModal
            isOpen={showBabyNamesModal}
            onClose={() => setShowBabyNamesModal(false)}
            babyNames={parsedBabyNames}
            childCount={currentPregnancy.child_count || 1}
            onSave={handleSaveBabyNames}
          />
          <DueDateModal
            isOpen={showDueDateModal}
            onClose={() => setShowDueDateModal(false)}
            currentDate={currentPregnancy.expected_due_date}
            onSave={handleSaveDueDate}
          />
          <EditPregnancyTypeModal
            isOpen={showEditPregnancyTypeModal}
            onClose={() => setShowEditPregnancyTypeModal(false)}
            currentChildCount={currentPregnancy.child_count || 1}
            pregnancyId={currentPregnancy.id}
            onSave={handleSavePregnancyType}
          />
        </>
      )}
      {/* Baby mode: Baby names modal for completed pregnancy */}
      {isBabyMode && !currentPregnancy && lastCompletedPregnancy && (
        <BabyNamesModal
          isOpen={showBabyNamesModal}
          onClose={() => setShowBabyNamesModal(false)}
          babyNames={babyModeBabyNames}
          childCount={babyModeChildCount}
          onSave={handleSaveBabyNamesBabyMode}
        />
      )}
      {currentPregnancy && (
        <CompletePregnancyModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onCompleteBirth={handleCompleteBirth}
          onDelete={handleDeletePregnancy}
          childCount={currentPregnancy.child_count || 1}
          babyNames={parsedBabyNames}
          isPremium={isPremium}
          premiumExpiresAt={premiumExpiresAt}
          onManageSubscription={handleManageSubscriptionForDelete}
        />
      )}
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        hasActiveSubscription={subscriptionStatus === 'active' || subscriptionStatus === 'trialing'}
        onManageSubscription={handleManageSubscriptionForDelete}
      />

      {/* Profile Edit Modals */}
      <EditNameModal
        isOpen={showEditNameModal}
        onClose={() => setShowEditNameModal(false)}
        currentFirstName={profileData.first_name}
        currentLastName={profileData.last_name}
        currentDisplayName={getMetadataDisplayName(user)}
        onSave={handleSaveName}
      />
      <EditGenderModal
        isOpen={showEditGenderModal}
        onClose={() => setShowEditGenderModal(false)}
        currentGender={profileData.gender}
        onSave={handleSaveGender}
      />
      <ChangeEmailModal
        isOpen={showChangeEmailModal}
        onClose={() => setShowChangeEmailModal(false)}
      />
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
      <ConsentManagementModal
        isOpen={showConsentModal}
        onClose={() => setShowConsentModal(false)}
      />
      <LanguageModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />
      <TimezoneModal
        isOpen={showTimezoneModal}
        onClose={() => setShowTimezoneModal(false)}
        currentTimezone={userTimezone}
        onSave={async (tz) => {
          if (!user?.id) return;
          await supabase.from('profiles').update({ timezone: tz }).eq('id', user.id);
          setUserTimezone(tz);
        }}
      />

      {/* Birth Data Modal for Baby Mode */}
      {isBabyMode && lastCompletedPregnancy && (
        <EditBirthDataModal
          isOpen={showBirthDataModal}
          onClose={() => setShowBirthDataModal(false)}
          onSave={handleSaveBirthData}
          currentBirthDate={lastCompletedPregnancy.actual_birth_date || null}
          currentBirthTime={lastCompletedPregnancy.birth_time || null}
          currentBabyData={lastCompletedPregnancy.baby_data || null}
          childCount={babyModeChildCount}
        />
      )}

      {/* Pregnancy Type Prompt Modal - rendered via portal for iOS z-index fix */}
      {showPregnancyTypePrompt && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={handlePregnancyTypePromptCancel} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-header">
                <h2>{t('profile:pregnancyTypePrompt.title')}</h2>
              </div>
              <div className="bottom-sheet-content">
                <p className="text-white/70 mb-4">
                  {t('profile:pregnancyTypePrompt.message')}
                </p>
              </div>
              <div className="bottom-sheet-footer">
                <button
                  onClick={handlePregnancyTypePromptCancel}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('profile:pregnancyTypePrompt.no')}
                </button>
                <button
                  onClick={handlePregnancyTypePromptConfirm}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
                >
                  {t('profile:pregnancyTypePrompt.yes')}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* New Pregnancy Confirmation Modal */}
      {showNewPregnancyConfirm && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={() => !isResettingData && setShowNewPregnancyConfirm(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-header">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  </div>
                  <h2>{t('profile:newPregnancy.title')}</h2>
                </div>
              </div>
              <div className="bottom-sheet-content">
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
                  <p className="text-red-300 font-medium mb-2">{t('profile:newPregnancy.warningTitle')}</p>
                  <p className="text-white/70 text-sm">
                    {t('profile:newPregnancy.warningMessage')}
                  </p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <p className="text-blue-300 font-medium mb-2">{t('profile:newPregnancy.inProgressTitle')}</p>
                  <p className="text-white/70 text-sm">
                    {t('profile:newPregnancy.inProgressMessage')}
                  </p>
                </div>
              </div>
              <div className="bottom-sheet-footer">
                <button
                  onClick={() => setShowNewPregnancyConfirm(false)}
                  disabled={isResettingData}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  onClick={handleStartNewPregnancy}
                  disabled={isResettingData}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isResettingData ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('profile:newPregnancy.deleting')}
                    </>
                  ) : (
                    t('profile:newPregnancy.confirmStart')
                  )}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// Profile Card Item (display only)
function ProfileItem({ label, value }: { label: string; value?: string }) {
  return (
    <div className="profile-card-item">
      <div className="profile-card-item-label">{label}</div>
      <div className="profile-card-item-value">{value || '—'}</div>
    </div>
  );
}

// Profile Card Item with Link
function ProfileItemLink({ label, value, onClick }: { label: string; value?: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="profile-card-item flex items-center justify-between w-full">
      <div>
        {value && <div className="profile-card-item-label">{label}</div>}
        <div className="profile-card-item-value">{value || label}</div>
      </div>
      <ChevronRight className="w-5 h-5 text-pp-orange" />
    </button>
  );
}
